// Contas e buscas dos indicadores novos do Comercial, do Marketing e da Diretoria (#6507).
// A regra de cada número está em src/lib/visaoGeral/comercialFase2.ts.
//
// Origem dos contratos e rede de parceiros saem das negociações que o Painel Executivo já
// carrega (mesma regra de aprovação do card de energia); qualidade do lead e custo por
// cliente fazem buscas pequenas, só da mídia paga.
import { useMemo } from 'react';
import { supabase } from '../../src/lib/supabase';
import type { Profile } from '../../types';
import { computeEventFunnel, type EventFunnelRow } from '../CRM/Analytics/eventFunnel';
import { aprovacaoAindaValida } from '../../src/lib/fechamentoMensal';
import { ETAPAS_QUALIFICADAS, contratosPorOrigem, qualidadeDoLead, redeDeParceiros } from '../../src/lib/visaoGeral/comercialFase2';
import { desempenhoDaMidia } from '../../src/lib/visaoGeral/tecnologia';
import { lerMes } from '../../src/lib/visaoGeral/periodo';
import { paginar, useBusca, useContratosDeMidia, useMidia } from './useTecnologiaMarketing';

const DIA_MS = 86_400_000;
export const DIAS_CONVERSAO_ORIGEM = 90;
export const ORIGEM_MIDIA = 'Mídia paga';

type Linha = EventFunnelRow & { origem?: string | null; responsavel_id?: string | null };

/** Meia-noite (horário local) do dia seguinte a 'AAAA-MM-DD'. */
const fimExclusivoMs = (dia: string) => { const [a, m, d] = dia.split('-').map(Number); return new Date(a, m - 1, d + 1).getTime(); };
const inicioMs = (dia: string) => { const [a, m, d] = dia.split('-').map(Number); return new Date(a, m - 1, d).getTime(); };

/**
 * Contratos aprovados no período por origem do lead, e a conversão de cada origem: dos leads
 * criados nos 90 dias até o fim do período (ou até hoje), quantos já estão aprovados.
 */
export function useOrigemDosContratos(dados: EventFunnelRow[], periodo: { inicio: string; fim: string }) {
  return useMemo(() => {
    if (dados.length === 0) return null;
    const aprovadas = (computeEventFunnel(dados, 'personalizado', { inicio: periodo.inicio, fim: periodo.fim })
      .find(e => e.key === 'termo_aprovado')?.rows ?? []) as Linha[];
    const fim = Math.min(fimExclusivoMs(periodo.fim), Date.now());
    const ini = fim - DIAS_CONVERSAO_ORIGEM * DIA_MS;
    const leads = (dados as Linha[]).filter(r => { const t = Date.parse(r.created_at); return t >= ini && t < fim; });
    return contratosPorOrigem(
      aprovadas.map(r => ({ origem: r.origem ?? null, kwh: r.consumo_medio_kwh ?? null })),
      leads.map(r => ({ origem: r.origem ?? null, aprovado: aprovacaoAindaValida(r) != null })),
    );
  }, [dados, periodo.inicio, periodo.fim]);
}

/** Rede de parceiros no período: parceiros (papel `parceiro`) e os vendedores ligados a eles. */
export function useRedeDeParceiros(dados: EventFunnelRow[], profilesById: Map<string, Profile>, periodo: { inicio: string; fim: string }) {
  return useMemo(() => {
    if (dados.length === 0 || profilesById.size === 0) return null;
    const perfis = [...profilesById.values()];
    const parceiros = perfis.filter(p => p.role === 'parceiro')
      .map(p => ({ id: String(p.id), nome: p.full_name ?? null, criado_em: p.created_at ?? null, status: p.status ?? null }));
    const vendedores = perfis.filter(p => p.parceiro_id).map(p => ({ id: String(p.id), parceiro_id: p.parceiro_id ?? null }));
    const negociacoes = (dados as Linha[]).map(r => ({
      responsavel_id: r.responsavel_id ?? null,
      criada_em: r.created_at,
      aprovadaEm: aprovacaoAindaValida(r),
      kwh: r.consumo_medio_kwh ?? null,
    }));
    return redeDeParceiros(parceiros, vendedores, negociacoes,
      new Date(inicioMs(periodo.inicio)).toISOString(), new Date(fimExclusivoMs(periodo.fim)).toISOString());
  }, [dados, profilesById, periodo.inicio, periodo.fim]);
}

/** Janela do mês 'AAAA-MM' e do anterior, em instantes. */
function janelas(mes: string) {
  const { ano, mes0 } = lerMes(mes)!;
  return {
    inicioAnterior: new Date(ano, mes0 - 1, 1).toISOString(),
    inicio: new Date(ano, mes0, 1).toISOString(),
    fim: new Date(ano, mes0 + 1, 1).toISOString(),
  };
}

/**
 * Qualidade do lead de mídia no mês e no anterior: leads com origem "Mídia paga" criados no
 * mês que chegaram pelo menos à Qualificação.
 */
export const useQualidadeDoLead = (mes: string) => useBusca(async () => {
  const j = janelas(mes);
  const [leads, eventos] = await Promise.all([
    paginar<{ id: number; stage: string | null; status: string | null; created_at: string }>(() => supabase.from('negociacoes')
      .select('id, stage, status, created_at')
      .eq('origem', ORIGEM_MIDIA)
      .gte('created_at', j.inicioAnterior).lt('created_at', j.fim)
      .order('id')),
    paginar<{ negociacao_id: number }>(() => supabase.from('negociacao_stage_events')
      .select('id, negociacao_id, negociacoes!inner(origem, created_at)')
      .in('to_stage', [...ETAPAS_QUALIFICADAS])
      .eq('negociacoes.origem', ORIGEM_MIDIA)
      .gte('negociacoes.created_at', j.inicioAnterior).lt('negociacoes.created_at', j.fim)
      .order('id')),
  ]);
  const passaram = new Set(eventos.map(e => Number(e.negociacao_id)));
  const corte = Date.parse(j.inicio);
  const doMes = leads.filter(l => Date.parse(l.created_at) >= corte);
  const anteriores = leads.filter(l => Date.parse(l.created_at) < corte);
  return { atual: qualidadeDoLead(doMes, passaram), anterior: qualidadeDoLead(anteriores, passaram) };
}, [mes], 'Qualidade do lead');

/**
 * Custo para trazer um cliente no mês: investimento no Meta Ads ÷ contratos de mídia paga
 * aprovados no mês (data da aprovação), e o mesmo no mês anterior.
 */
export function useCustoPorCliente(mes: string) {
  const j = useMemo(() => janelas(mes), [mes]);
  const dias = useMemo(() => {
    const { ano, mes0 } = lerMes(mes)!;
    const dia = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { desde: dia(new Date(ano, mes0 - 1, 1)), inicioMes: dia(new Date(ano, mes0, 1)), ate: dia(new Date(ano, mes0 + 1, 0)) };
  }, [mes]);
  const midia = useMidia(dias.desde, dias.ate, false);
  const contratos = useContratosDeMidia(j.inicioAnterior, j.fim);
  return useMemo(() => {
    if (!midia.dados || !contratos.dados) return null;
    const conta = (doMes: boolean) => {
      const gasto = desempenhoDaMidia(midia.dados!.campanhas.filter(l => (l.date >= dias.inicioMes) === doMes)).gasto;
      const lista = contratos.dados!.filter(c => (Date.parse(c.aprovadaEm) >= Date.parse(j.inicio)) === doMes);
      return { gasto, contratos: lista.length, kwh: lista.reduce((s, c) => s + c.kwh, 0), custo: lista.length ? gasto / lista.length : null };
    };
    return { atual: conta(true), anterior: conta(false) };
  }, [midia.dados, contratos.dados, dias.inicioMes, j.inicio]);
}
